/** ACCOUNT — the signed-in user's own record. */
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { GET_PROFILE, MY_ACCOUNT_RESIDENCY } from '../queries';

/**
 * Who is signed in, from the backend rather than the token.
 *
 * The login mutation returns an id and nothing else about the person, so the
 * name, email and verification state come from here. Errors are kept rather
 * than thrown: a profile screen with a dead call should say so, not blank.
 */
export function useProfile() {
  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);
  const { data, loading, error } = useQuery(GET_PROFILE, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return { loading, error, user: data?.getProfile ?? null };
}

/**
 * The residency ids on the signed-in account, so a partial `updateProfile` can
 * send them back rather than erase them — see MY_ACCOUNT_RESIDENCY. Skipped
 * until a user id is known; answers `{}` when the account has neither set.
 */
export function useMyResidency() {
  const userId = useSelector((s) => s.auth.userId);
  const numeric = Number(userId);
  const skip = !userId || Number.isNaN(numeric);
  const { data, loading } = useQuery(MY_ACCOUNT_RESIDENCY, {
    variables: { ids: skip ? [] : [numeric] },
    skip,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const row = (data?.getAllUsers?.items ?? []).find((u) => String(u.id) === String(numeric)) ?? null;
  return {
    loading,
    /* Spread into an updateProfile input. Absent keys would be nulled by the
       backend; explicit nulls are what these fields already are. */
    keep: {
      primaryResidencyId: row?.primaryResidencyId ?? null,
      investmentOptimizedResidencyId: row?.investmentOptimizedResidencyId ?? null,
    },
  };
}
